import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaCheckCircle,
  FaStar,
  FaPhoneAlt,
  FaEnvelope,
  FaGlobe,
} from "react-icons/fa";
import {
  createServiceEngagement,
  getClientVendorEngagement,
  postClientReview,
} from "../api/serviceEngagements";
import { get } from "../services";

const TABS = ["Business Listings", "Services", "Engagements", "About"];

const BusinessListingDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [vendor, setVendor] = useState<any>(location.state?.biz || null);
  const [loadingVendor, setLoadingVendor] = useState(!location.state?.biz);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedListingId, setSelectedListingId] = useState<number | null>(
    null
  );
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    serviceQualityRating: 0,
    communicationRating: 0,
    valueForMoneyRating: 0,
    comment: "",
    wouldRecommend: false,
  });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState("");
  const [reviewError, setReviewError] = useState("");
  // State for image modal
  const [modalImage, setModalImage] = useState<string | null>(null);
  // State for business listing modal
  const [modalListing, setModalListing] = useState<any | null>(null);
  const [bookingModal, setBookingModal] = useState<{
    open: boolean;
    biz: any | null;
  }>({ open: false, biz: null });
  const [bookingForm, setBookingForm] = useState({
    inquiryMessage: "",
    preferredServiceDate: "",
    estimatedPrice: "",
    currency: "NGN",
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState("");
  const [lastEngagementId, setLastEngagementId] = useState<string | null>(null);
  const [engagements, setEngagements] = useState<any[]>([]);
  const [reviewModal, setReviewModal] = useState<{
    open: boolean;
    engagement: any | null;
  }>({ open: false, engagement: null });
  const [similarVendors, setSimilarVendors] = useState<any[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState("");

  // Fetch vendor details if not in location.state
  useEffect(() => {
    if (!vendor && id) {
      setLoadingVendor(true);
      get(`/api/vendors/${id}/full-details`)
        .then((data) => {
          // Defensive fallback: ensure all expected fields exist
          const safeVendor = {
            ...data?.data,
            profileImageUrl:
              data?.data?.profileImageUrl || data?.data?.avatar || "",
            avatar: data?.data?.avatar || "",
            name: data?.data?.name || "",
            isVerified: !!data?.data?.isVerified,
            rating: data?.data?.rating || 0,
            reviews: data?.data?.reviews || [],
            businessListings: Array.isArray(data?.data?.businessListings)
              ? data.data.businessListings
              : [],
            city: data?.data?.city || "",
            state: data?.data?.state || "",
            country: data?.data?.country || "",
            bio: data?.data?.bio || "",
            website: data?.data?.website || "",
            userEmail: data?.data?.userEmail || "",
          };
          setVendor(safeVendor);
        })
        .finally(() => setLoadingVendor(false));
    }
  }, [id, vendor]);

  useEffect(() => {
    const firstListingId = vendor?.businessListings?.[0]?.id;
    if (firstListingId && selectedListingId === null) {
      setSelectedListingId(firstListingId);
    }
  }, [vendor?.businessListings, selectedListingId]);

  useEffect(() => {
    if (!vendor) return;
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const clientId = user?.actorId;
    if (!clientId || !vendor?.id) return;
    getClientVendorEngagement(clientId, vendor.id)
      .then((res) => {
        setEngagements(res?.data?.content || []);
        const engagement = res?.data?.content?.[0];
        if (engagement?.id) setLastEngagementId(engagement.id.toString());
      })
      .catch(() => {});
  }, [vendor]);

  useEffect(() => {
    if (!vendor?.id) return;
    setSimilarLoading(true);
    setSimilarError("");
    get(`/api/vendors/${vendor.id}/similar?limit=5`)
      .then((res: any) => {
        const payload = res?.data || [];
        setSimilarVendors(Array.isArray(payload) ? payload : []);
      })
      .catch(() =>
        setSimilarError("Unable to load similar vendors at the moment.")
      )
      .finally(() => setSimilarLoading(false));
  }, [vendor?.id]);

  if (loadingVendor) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ margin: "0 auto 16px", display: "block" }}
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#6C63FF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="31.416"
            strokeDashoffset="31.416"
            fill="none"
            opacity="0.3"
          >
            <animate
              attributeName="stroke-dasharray"
              dur="2s"
              values="0 31.416;15.708 15.708;0 31.416;0 31.416"
              repeatCount="indefinite"
            />
            <animate
              attributeName="stroke-dashoffset"
              dur="2s"
              values="0;-15.708;-31.416;-31.416"
              repeatCount="indefinite"
            />
          </circle>
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#6C63FF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="31.416"
            strokeDashoffset="15.708"
            fill="none"
          >
            <animate
              attributeName="stroke-dasharray"
              dur="2s"
              values="0 31.416;15.708 15.708;0 31.416;0 31.416"
              repeatCount="indefinite"
            />
            <animate
              attributeName="stroke-dashoffset"
              dur="2s"
              values="0;-15.708;-31.416;-31.416"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
        <h2 style={{ color: "#333", fontWeight: 600, fontSize: 20 }}>
          Loading vendor details...
        </h2>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ margin: "0 auto 16px", display: "block", opacity: 0.5 }}
        >
          <path
            d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
            stroke="#888"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <circle
            cx="12"
            cy="10"
            r="3"
            stroke="#888"
            strokeWidth="2"
            fill="none"
          />
        </svg>
        <h2
          style={{
            color: "#222",
            fontWeight: 700,
            fontSize: 24,
            marginBottom: 8,
          }}
        >
          Business Listing Not Found
        </h2>
        <p
          style={{
            color: "#666",
            fontSize: 16,
            marginBottom: 24,
            fontWeight: 400,
          }}
        >
          The vendor you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "12px 32px",
            borderRadius: 8,
            background: "#6C63FF",
            color: "#fff",
            border: "none",
            fontWeight: 700,
            fontSize: 16,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(108, 99, 255, 0.3)",
            transition: "all 0.2s",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = "#5a52e5";
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(108, 99, 255, 0.4)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = "#6C63FF";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 2px 8px rgba(108, 99, 255, 0.3)";
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Aggregate all services from all business listings
  const allServices = (vendor.businessListings || []).flatMap(
    (b: any) => b.services || []
  );
  const heroBackground =
    vendor?.heroImageUrl ||
    vendor?.coverImageUrl ||
    vendor?.businessListings?.[0]?.images?.[0]?.imageUrl ||
    vendor?.profileImageUrl ||
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80";

  const listings = vendor.businessListings || [];
  const selectedListing =
    listings.find((b: any) => b?.id === selectedListingId) || listings[0] || null;

  const formatSimilarName = (item: any) =>
    `${item?.userFirstName || item?.name || "Event"} ${
      item?.userLastName || "Expert"
    }`.trim();

  const formatSimilarLocation = (item: any) => {
    const parts = [item?.city, item?.state, item?.country].filter(Boolean);
    return parts.join(", ") || "Nigeria";
  };

  const getSimilarAvatar = (item: any) =>
    item?.profileImageUrl ||
    `https://ui-avatars.com/api/?background=6C63FF&color=fff&name=${encodeURIComponent(
      formatSimilarName(item)
    )}`;

  const handleScrollTo = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePrimaryBooking = () => {
    setBookingModal({
      open: true,
      biz: vendor.businessListings?.[0] || null,
    });
  };

  const handleStartEngagement = () => {
    if (lastEngagementId) {
      navigate(`/engagement-chat/${lastEngagementId}`);
    } else {
      alert(
        "You don't have an active engagement with this vendor yet. Please book a consultation to get started."
      );
    }
  };

  const handleViewServices = () => {
    if (allServices.length === 0) {
      alert(
        "This vendor has not published any services yet. Please contact them directly for more information."
      );
      return;
    }
    setActiveTab(0);
    handleScrollTo("listings-section");
  };

  const handleViewEngagements = () => {
    setActiveTab(2);
    handleScrollTo("engagements-section");
  };

  return (
    <div style={{ background: "#050814" }}>
      <section
        style={{
          position: "relative",
          backgroundImage: `linear-gradient(120deg, rgba(5,8,20,0.85), rgba(15,23,42,0.85)), url(${heroBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
        }}
      >
    <div
      style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "80px 20px 140px",
            color: "#fff",
            display: "flex",
            flexWrap: "wrap",
            gap: 40,
          }}
        >
          <div style={{ flex: "1 1 520px" }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {vendor.isVerified && (
                <span
                  style={{
                    padding: "8px 16px",
                    borderRadius: 999,
                    background: "rgba(40,167,69,0.2)",
                    color: "#4ade80",
                    fontWeight: 600,
                    fontSize: 13,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Verified Vendor
                </span>
              )}
              {vendor.businessListings?.[0]?.category && (
                <span
                  style={{
                    padding: "8px 16px",
                    borderRadius: 999,
                    background: "rgba(255,215,0,0.25)",
                    color: "#fde68a",
                    fontWeight: 600,
                    fontSize: 13,
                    textTransform: "uppercase",
                  }}
                >
                  {vendor.businessListings[0].category}
                </span>
              )}
            </div>
            <h1
              style={{
                fontSize: 48,
                fontWeight: 900,
                margin: "20px 0 12px",
                letterSpacing: -1,
              }}
            >
              {vendor.name}
            </h1>
            <p
              style={{
                fontSize: 18,
                lineHeight: 1.7,
                color: "rgba(255,255,255,0.9)",
                maxWidth: 640,
                marginBottom: 24,
              }}
            >
              {vendor.bio ||
                "Crafting unforgettable experiences with seamless planning, curated talent, and signature aesthetics tailored to your story."}
            </p>
            <div
              style={{
                display: "flex",
                gap: 18,
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                <FaStar color="#FFD700" /> {(vendor.rating || 0).toFixed(1)}{" "}
                <span style={{ fontWeight: 400, fontSize: 14 }}>
                  ({vendor.reviews?.length || 0} reviews)
                </span>
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 15,
                  opacity: 0.85,
                }}
              >
                <FaMapMarkerAlt />
                {vendor.city && vendor.state
                  ? `${vendor.city}, ${vendor.state}`
                  : vendor.city || vendor.state || "Nigeria"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
                marginBottom: 20,
              }}
            >
              <button
                onClick={handlePrimaryBooking}
                style={{
                  background:
                    "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                  color: "#1f2937",
                  border: "none",
                  borderRadius: 999,
                  padding: "12px 32px",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: "pointer",
                  boxShadow: "0 15px 40px rgba(251,191,36,0.35)",
                }}
              >
                Book Consultation
              </button>
              <button
                onClick={handleViewServices}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: 999,
                  padding: "12px 28px",
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                View Services
              </button>
              <button
                onClick={handleViewEngagements}
                style={{
                  background: "none",
                  border: "none",
                  color: "#93c5fd",
                  fontWeight: 600,
                  cursor: "pointer",
                  textDecoration: "underline",
                  padding: "12px 0",
                }}
              >
                See recent engagements
              </button>
              <button
                onClick={handleStartEngagement}
                style={{
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: 999,
                  padding: "12px 28px",
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                {lastEngagementId ? "Go to Engagement" : "Start Engagement"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {vendor.userEmail && (
                <a
                  href={`mailto:${vendor.userEmail}`}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "#fff",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  {vendor.userEmail}
                </a>
              )}
              {vendor.businessListings?.[0]?.contactPhone && (
                <a
                  href={`tel:${vendor.businessListings[0].contactPhone}`}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "#fff",
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  {vendor.businessListings[0].contactPhone}
                </a>
              )}
            </div>
          </div>
          <div
            style={{
              flex: "1 1 320px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 16,
            }}
          >
            {[
              {
                label: "Experience",
                value: vendor.yearsOfExperience
                  ? `${vendor.yearsOfExperience}+ yrs`
                  : "Pro Grade",
              },
              {
                label: "Avg. Rating",
                value: `${(vendor.rating || 0).toFixed(1)}/5`,
              },
              {
                label: "Response",
                value:
                  vendor.responseTime?.length > 12
                    ? `${vendor.responseTime.slice(0, 12)}…`
                    : vendor.responseTime || "~1 hr",
              },
              {
                label: "Engagements",
                value: vendor.completedEngagements
                  ? `${vendor.completedEngagements}+`
                  : "Hundreds",
              },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: 16,
                  padding: 18,
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  minHeight: 88,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(18px, 2.2vw, 26px)",
                    fontWeight: 900,
                    marginBottom: 6,
                    color: "#fff",
                    lineHeight: 1.1,
                    wordBreak: "break-word",
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    opacity: 0.8,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div
        style={{
          background: "linear-gradient(120deg, #edf2ff 0%, #ffffff 100%)",
          padding: "0 0 80px",
      }}
    >
      <div
        style={{
          maxWidth: 1300,
            margin: "-110px auto 0 auto",
            background: "#fff",
            borderRadius: 32,
            padding: 32,
            boxShadow: "0 40px 120px rgba(5,8,20,0.35)",
          }}
        >
          <div
            style={{
          display: "flex",
          gap: 40,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {/* Side Pane */}
        <div
          style={{
            flex: "0 0 340px",
            minWidth: 280,
            maxWidth: 340,
            minHeight: 552,
            background: "rgba(255,255,255,0.85)",
          borderRadius: 24,
          boxShadow: "0 4px 24px #eee",
          padding: 32,
          display: "flex",
            flexDirection: "column",
          alignItems: "center",
            position: "sticky",
            top: 32,
            zIndex: 2,
        }}
      >
          {vendor.profileImageUrl || vendor.avatar ? (
            <img
              src={vendor.profileImageUrl || vendor.avatar}
              alt={vendor.name}
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                objectFit: "cover",
                border: "4px solid #FFD700",
                background: "#fff",
                boxShadow: "0 0 0 4px rgba(108,99,255,0.10)",
                marginBottom: 16,
              }}
            />
          ) : (
            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: "#FFD700",
                color: "#6C63FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 38,
                fontWeight: 900,
                border: "4px solid #FFD700",
                boxShadow: "0 0 0 4px rgba(108,99,255,0.10)",
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              {vendor.name.charAt(0)}
            </div>
          )}
          {vendor.isVerified && (
            <span
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "#fff",
                borderRadius: "50%",
                boxShadow: "0 2px 8px #eee",
                padding: 2,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaCheckCircle
                color="#28a745"
                title="Verified"
                style={{ fontSize: 18 }}
              />
            </span>
          )}
          <h2
            style={{
              fontWeight: 900,
              fontSize: 24,
              margin: "16px 0 0 0",
              textAlign: "center",
            }}
          >
            {vendor.name}
          </h2>
          <div
            style={{
              color: "#6C63FF",
              fontWeight: 700,
              fontSize: 15,
              margin: "8px 0 10px 0",
              textAlign: "center",
            }}
          >
            Event Vendor
            {vendor.yearsOfExperience
              ? ` • ${vendor.yearsOfExperience} yrs exp.`
              : ""}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
              justifyContent: "center",
            }}
          >
            <span style={{ color: "#FFD700", fontSize: 18 }}>
              <FaStar style={{ marginRight: 2 }} />
              {vendor.rating || 0}
            </span>
                <span style={{ color: "#666", fontSize: 14, fontWeight: 400 }}>
              ({vendor.reviews || 0})
            </span>
          </div>
          <div
            style={{
              color: "#6C63FF",
              fontWeight: 600,
              fontSize: 15,
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
              justifyContent: "center",
            }}
          >
            <FaMapMarkerAlt /> {vendor.city || vendor.location || ""}
          </div>
          <div
            style={{
                  color: "#333",
              fontSize: 15,
              marginBottom: 10,
                  lineHeight: 1.6,
              textAlign: "center",
                  fontWeight: 400,
            }}
          >
            {vendor.bio}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              width: "100%",
              marginTop: 12,
            }}
          >
            {vendor.website && (
            <a
                href={vendor.website}
                target="_blank"
                rel="noopener noreferrer"
              style={{
                color: "#6C63FF",
                textDecoration: "none",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
                  justifyContent: "center",
              }}
            >
                <FaGlobe /> Website
            </a>
            )}
            {vendor.userEmail && (
            <a
                href={`mailto:${vendor.userEmail}`}
              style={{
                color: "#6C63FF",
                textDecoration: "none",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
                  justifyContent: "center",
              }}
            >
                <FaEnvelope /> {vendor.userEmail}
            </a>
            )}
            {vendor.businessListings &&
              vendor.businessListings[0] &&
              vendor.businessListings[0].contactPhone && (
            <a
                  href={`tel:${vendor.businessListings[0].contactPhone}`}
              style={{
                color: "#6C63FF",
                textDecoration: "none",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
                    justifyContent: "center",
              }}
            >
                  <FaPhoneAlt /> {vendor.businessListings[0].contactPhone}
            </a>
              )}
          </div>
        </div>
        {/* Main Content with Tabs */}
        <div style={{ flex: 1, minWidth: 320 }}>
          {/* Tabs */}
      <div
        style={{
              display: "flex",
              gap: 32,
              borderBottom: "2px solid #eee",
              marginBottom: 32,
        }}
      >
            {TABS.map((tab, idx) => (
        <button
                key={tab}
                onClick={() => setActiveTab(idx)}
          style={{
                  background: "none",
            border: "none",
                  borderBottom:
                    activeTab === idx
                      ? "3px solid #6C63FF"
                      : "3px solid transparent",
                      color: activeTab === idx ? "#6C63FF" : "#666",
                  fontWeight: activeTab === idx ? 800 : 600,
            fontSize: 18,
                  padding: "12px 0",
            cursor: "pointer",
                  outline: "none",
                  transition: "color 0.2s, border-bottom 0.2s",
          }}
        >
                {tab}
        </button>
            ))}
      </div>
          {/* Tab Content */}
          <div>
            {/* Business Listings Tab */}
            {activeTab === 0 && (
              <div id="listings-section">
                <div
                  style={{
                    display: "flex",
                    gap: 18,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Listing selector */}
                  <div
                    style={{
                      flex: "0 0 320px",
                      minWidth: 280,
                      maxWidth: 360,
                      alignSelf: "flex-start",
                    }}
                    className="en-listing-selector"
                  >
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: 18,
                        boxShadow: "0 8px 28px rgba(2, 6, 23, 0.08)",
                        border: "1px solid rgba(99, 102, 241, 0.12)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "16px 16px 12px",
                          borderBottom: "1px solid #f0f2ff",
                          background:
                            "linear-gradient(135deg, rgba(108,99,255,0.08), rgba(139,92,246,0.04))",
                        }}
                      >
                        <div
                          style={{
                            color: "#111827",
                            fontWeight: 900,
                            fontSize: 16,
                            letterSpacing: "-0.2px",
                          }}
                        >
                          Business Listings
                        </div>
                        <div style={{ color: "#6b7280", fontWeight: 600, fontSize: 13, marginTop: 4 }}>
                          {listings.length} listing{listings.length === 1 ? "" : "s"} •{" "}
                          {allServices.length} service{allServices.length === 1 ? "" : "s"}
                        </div>
                      </div>

                      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                        {listings.map((biz: any) => {
                          const isActive = biz?.id === (selectedListing?.id ?? selectedListingId);
                          const img = biz?.images?.find((i: any) => i?.isPrimary)?.imageUrl || biz?.images?.[0]?.imageUrl || null;
                          const serviceCount = (biz?.services || []).length;
                          const imageCount = (biz?.images || []).length;
                          return (
                            <button
                              key={biz.id}
                              onClick={() => setSelectedListingId(biz.id)}
                              style={{
                                textAlign: "left",
                                border: isActive
                                  ? "2px solid rgba(108,99,255,0.6)"
                                  : "1px solid rgba(17,24,39,0.08)",
                                background: isActive
                                  ? "linear-gradient(135deg, rgba(108,99,255,0.10), rgba(139,92,246,0.06))"
                                  : "#fff",
                                borderRadius: 14,
                                padding: 12,
                                cursor: "pointer",
                                display: "flex",
                                gap: 12,
                                alignItems: "center",
                                boxShadow: isActive
                                  ? "0 10px 28px rgba(108,99,255,0.18)"
                                  : "0 2px 10px rgba(2,6,23,0.06)",
                                transition: "all 0.18s ease",
                              }}
                            >
                              <div
                                style={{
                                  width: 52,
                                  height: 52,
                                  borderRadius: 12,
                                  background: img
                                    ? `url(${img}) center/cover no-repeat`
                                    : "linear-gradient(135deg, #e0e7ff 0%, #f5f3ff 100%)",
                                  flexShrink: 0,
                                  border: "1px solid rgba(17,24,39,0.08)",
                                }}
                              />
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    fontWeight: 900,
                                    color: "#111827",
                                    fontSize: 14,
                                    lineHeight: 1.2,
                                    marginBottom: 4,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {biz.businessName || "Business Listing"}
                                </div>
                                <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {biz.category || "Category"} • {biz.city || ""}{biz.state ? `, ${biz.state}` : ""}
                                </div>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 900,
                                      color: "#4f46e5",
                                      background: "rgba(79,70,229,0.10)",
                                      padding: "4px 8px",
                                      borderRadius: 999,
                                    }}
                                  >
                                    {serviceCount} service{serviceCount === 1 ? "" : "s"}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 900,
                                      color: "#7c3aed",
                                      background: "rgba(124,58,237,0.10)",
                                      padding: "4px 8px",
                                      borderRadius: 999,
                                    }}
                                  >
                                    {imageCount} image{imageCount === 1 ? "" : "s"}
                                  </span>
                                  {biz.isFeatured && (
                                    <span
                                      style={{
                                        fontSize: 11,
                                        fontWeight: 900,
                                        color: "#92400e",
                                        background: "rgba(245,158,11,0.18)",
                                        padding: "4px 8px",
                                        borderRadius: 999,
                                      }}
                                    >
                                      Featured
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Selected listing detail */}
                  <div style={{ flex: "1 1 520px", minWidth: 320 }}>
                    {!selectedListing ? (
                      <div style={{ color: "#666", fontSize: 16, fontWeight: 500 }}>
                        No business listing found.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <div
                          style={{
                            background: "#fff",
                            borderRadius: 20,
                            boxShadow: "0 12px 40px rgba(2,6,23,0.08)",
                            border: "1px solid rgba(99, 102, 241, 0.12)",
                            padding: 18,
                          }}
                        >
                          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                            <div style={{ flex: 1, minWidth: 240 }}>
                              <div style={{ color: "#111827", fontWeight: 1000, fontSize: 22, letterSpacing: "-0.5px" }}>
                                {selectedListing.businessName || "Business Listing"}
                              </div>
                              <div style={{ marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                                {selectedListing.category && (
                                  <span
                                    style={{
                                      padding: "6px 10px",
                                      borderRadius: 999,
                                      background: "rgba(108,99,255,0.10)",
                                      color: "#4f46e5",
                                      fontWeight: 900,
                                      fontSize: 12,
                                    }}
                                  >
                                    {selectedListing.category}
                                  </span>
                                )}
                                <span style={{ color: "#6b7280", fontWeight: 800, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  <FaMapMarkerAlt color="#6C63FF" />{" "}
                                  {[selectedListing.city, selectedListing.state, selectedListing.country]
                                    .filter(Boolean)
                                    .join(", ") || "Nigeria"}
                                </span>
                              </div>
                            </div>

                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                              <button
                                onClick={() => setModalListing(selectedListing)}
                                style={{
                                  background: "rgba(108,99,255,0.10)",
                                  color: "#4f46e5",
                                  border: "1px solid rgba(108,99,255,0.35)",
                                  borderRadius: 12,
                                  padding: "10px 12px",
                                  fontWeight: 900,
                                  cursor: "pointer",
                                }}
                              >
                                View details
                              </button>
                              <button
                                onClick={() => setBookingModal({ open: true, biz: selectedListing })}
                                style={{
                                  background: "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 12,
                                  padding: "10px 14px",
                                  fontWeight: 1000,
                                  cursor: "pointer",
                                  boxShadow: "0 10px 26px rgba(108,99,255,0.25)",
                                }}
                              >
                                Book this listing
                              </button>
                            </div>
                          </div>

                          {selectedListing.description && (
                            <div style={{ marginTop: 12, color: "#374151", fontWeight: 600, lineHeight: 1.6 }}>
                              {selectedListing.description}
                            </div>
                          )}

                          {(selectedListing.contactPhone || selectedListing.contactEmail) && (
                            <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                              {selectedListing.contactPhone && (
                                <a
                                  href={`tel:${selectedListing.contactPhone}`}
                                  style={{
                                    textDecoration: "none",
                                    color: "#111827",
                                    background: "#f8fafc",
                                    border: "1px solid rgba(17,24,39,0.08)",
                                    padding: "8px 10px",
                                    borderRadius: 12,
                                    fontWeight: 800,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <FaPhoneAlt color="#6C63FF" /> {selectedListing.contactPhone}
                                </a>
                              )}
                              {selectedListing.contactEmail && (
                                <a
                                  href={`mailto:${selectedListing.contactEmail}`}
                                  style={{
                                    textDecoration: "none",
                                    color: "#111827",
                                    background: "#f8fafc",
                                    border: "1px solid rgba(17,24,39,0.08)",
                                    padding: "8px 10px",
                                    borderRadius: 12,
                                    fontWeight: 800,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <FaEnvelope color="#6C63FF" /> {selectedListing.contactEmail}
                                </a>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Images */}
                        <div
                          style={{
                            background: "#fff",
                            borderRadius: 20,
                            boxShadow: "0 12px 40px rgba(2,6,23,0.06)",
                            border: "1px solid rgba(17,24,39,0.08)",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              padding: "14px 16px",
                              borderBottom: "1px solid rgba(17,24,39,0.08)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 12,
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ fontWeight: 1000, color: "#111827" }}>Listing images</div>
                            <div style={{ color: "#6b7280", fontWeight: 800, fontSize: 13 }}>
                              {(selectedListing.images || []).length} image
                              {(selectedListing.images || []).length === 1 ? "" : "s"}
                            </div>
                          </div>

                          {(!selectedListing.images || selectedListing.images.length === 0) ? (
                            <div style={{ padding: 16, color: "#6b7280", fontWeight: 700 }}>
                              No images uploaded for this listing yet.
                            </div>
                          ) : (
                            <div style={{ padding: 16 }}>
                              {(() => {
                                const imgs = selectedListing.images || [];
                                const primary =
                                  imgs.find((i: any) => i?.isPrimary)?.imageUrl ||
                                  imgs[0]?.imageUrl;
                                const rest = imgs.filter((i: any) => i?.imageUrl && i.imageUrl !== primary);
                                return (
                                  <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1.6fr) minmax(180px, 1fr)", gap: 12 }}>
                                    <div>
                                      <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => primary && setModalImage(primary)}
                                        style={{
                                          width: "100%",
                                          height: 320,
                                          borderRadius: 16,
                                          background: `url(${primary}) center/cover no-repeat`,
                                          cursor: "pointer",
                                          border: "1px solid rgba(17,24,39,0.08)",
                                          boxShadow: "0 10px 24px rgba(2,6,23,0.10)",
                                        }}
                                      />
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignContent: "start" }}>
                                      {rest.slice(0, 6).map((img: any, idx: number) => (
                                        <div
                                          key={idx}
                                          role="button"
                                          tabIndex={0}
                                          onClick={() => setModalImage(img.imageUrl)}
                                          style={{
                                            width: "100%",
                                            height: 98,
                                            borderRadius: 14,
                                            background: `url(${img.imageUrl}) center/cover no-repeat`,
                                            cursor: "pointer",
                                            border: "1px solid rgba(17,24,39,0.08)",
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>

                        {/* Services */}
                        <div
                          style={{
                            background: "#fff",
                            borderRadius: 20,
                            boxShadow: "0 12px 40px rgba(2,6,23,0.06)",
                            border: "1px solid rgba(17,24,39,0.08)",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              padding: "14px 16px",
                              borderBottom: "1px solid rgba(17,24,39,0.08)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 12,
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ fontWeight: 1000, color: "#111827" }}>Services in this listing</div>
                            <div style={{ color: "#6b7280", fontWeight: 800, fontSize: 13 }}>
                              {(selectedListing.services || []).length} service
                              {(selectedListing.services || []).length === 1 ? "" : "s"}
                            </div>
                          </div>

                          {(selectedListing.services || []).length === 0 ? (
                            <div style={{ padding: 16, color: "#6b7280", fontWeight: 700 }}>
                              No services added for this listing yet.
                            </div>
                          ) : (
                            <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
                              {(selectedListing.services || []).map((s: any) => (
                                <div
                                  key={s.id || `${s.serviceName}-${s.createdAt}`}
                                  style={{
                                    border: "1px solid rgba(99,102,241,0.14)",
                                    background: "linear-gradient(135deg, rgba(108,99,255,0.06), rgba(139,92,246,0.03))",
                                    borderRadius: 16,
                                    padding: 14,
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontWeight: 1000, color: "#111827", fontSize: 15, lineHeight: 1.2 }}>
                                        {s.serviceName || "Service"}
                                      </div>
                                      <div style={{ marginTop: 6, color: "#6b7280", fontWeight: 800, fontSize: 12 }}>
                                        {(s.subcategory?.name || s.category?.name || selectedListing.category || "Category")}
                                        {typeof s.durationHours === "number" ? ` • ${s.durationHours}h` : ""}
                                      </div>
                                    </div>
                                    <span
                                      style={{
                                        padding: "6px 10px",
                                        borderRadius: 999,
                                        background: "rgba(16,185,129,0.10)",
                                        color: "#047857",
                                        fontWeight: 1000,
                                        fontSize: 12,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {s.pricingType || "PRICING"}
                                    </span>
                                  </div>
                                  {s.description && (
                                    <div style={{ marginTop: 10, color: "#374151", fontWeight: 650, lineHeight: 1.55, fontSize: 13 }}>
                                      {s.description}
                                    </div>
                                  )}
                                  <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    {typeof s.minAdvanceBookingHours === "number" && (
                                      <span style={{ fontSize: 11, fontWeight: 900, color: "#4f46e5", background: "rgba(79,70,229,0.10)", padding: "4px 8px", borderRadius: 999 }}>
                                        Min lead: {s.minAdvanceBookingHours}h
                                      </span>
                                    )}
                                    {typeof s.maxAdvanceBookingDays === "number" && (
                                      <span style={{ fontSize: 11, fontWeight: 900, color: "#7c3aed", background: "rgba(124,58,237,0.10)", padding: "4px 8px", borderRadius: 999 }}>
                                        Max lead: {s.maxAdvanceBookingDays}d
                                      </span>
                                    )}
                                    {s.isAvailable === false && (
                                      <span style={{ fontSize: 11, fontWeight: 900, color: "#b91c1c", background: "rgba(248,113,113,0.14)", padding: "4px 8px", borderRadius: 999 }}>
                                        Unavailable
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <style>{`
                  @media (max-width: 900px) {
                    .en-listing-selector {
                      position: relative !important;
                      top: auto !important;
                      flex: 1 1 100% !important;
                      max-width: none !important;
                    }
                  }
                `}</style>
              </div>
            )}
            {/* Services Tab */}
            {activeTab === 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
                {allServices.length === 0 ? (
                      <div
                        style={{ color: "#666", fontSize: 16, fontWeight: 400 }}
                      >
                    No services found.
                  </div>
                ) : (
                  allServices.map((s: any, i: number) => (
        <div
                      key={i}
          style={{
            background: "#fff",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px #eee",
                        padding: 20,
                        minWidth: 220,
                        maxWidth: 300,
                        flex: "1 1 220px",
            display: "flex",
                        flexDirection: "column",
                        gap: 8,
          }}
        >
            <div
              style={{
                          color: "#6C63FF",
                          fontWeight: 700,
                          fontSize: 16,
                    }}
                      >
                        {s.serviceName}
            </div>
                          <div
                            style={{
                              color: "#333",
                              fontSize: 14,
                              fontWeight: 400,
                              lineHeight: 1.5,
                            }}
                          >
                        {s.description}
          </div>
            <div
              style={{
                          color: "#FFD700",
                fontWeight: 600,
                          fontSize: 15,
              }}
            >
                        {s.pricingType}
            </div>
            </div>
                  ))
                )}
              </div>
            )}
            {/* Reviews Tab */}
            {activeTab === 2 && (
              <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 24,
                    }}
              >
                {engagements.length === 0 ? (
                      <div
                        style={{ color: "#666", fontSize: 16, fontWeight: 400 }}
                      >
                    No engagements yet.
                  </div>
                ) : (
                  engagements.map((eng: any) => (
            <div
                      key={eng.id}
              style={{
                        background: "#fff",
                        borderRadius: 16,
                        boxShadow: "0 2px 8px #eee",
                        padding: 24,
                display: "flex",
                        flexDirection: "column",
                        gap: 8,
              }}
            >
                      <div
                      style={{
                          fontWeight: 700,
                        color: "#6C63FF",
                          fontSize: 17,
                        }}
                      >
                        Engagement #{eng.id}
                      </div>
                          <div
                            style={{
                              color: "#333",
                              fontSize: 15,
                              fontWeight: 400,
                            }}
                          >
                            <strong style={{ color: "#222", fontWeight: 600 }}>
                              Status:
                            </strong>{" "}
                            {eng.status}
                      </div>
                          <div
                            style={{
                              color: "#333",
                              fontSize: 15,
                              fontWeight: 400,
                            }}
                          >
                            <strong style={{ color: "#222", fontWeight: 600 }}>
                              Service:
                            </strong>{" "}
                        {eng.businessService?.serviceName}
                      </div>
                          <div
                            style={{
                              color: "#333",
                              fontSize: 15,
                              fontWeight: 400,
                              lineHeight: 1.5,
                            }}
                          >
                            <strong style={{ color: "#222", fontWeight: 600 }}>
                              Inquiry:
                            </strong>{" "}
                            {eng.inquiryMessage}
                      </div>
                          <div
                            style={{
                              color: "#333",
                              fontSize: 15,
                              fontWeight: 400,
                            }}
                          >
                            <strong style={{ color: "#222", fontWeight: 600 }}>
                              Date:
                            </strong>{" "}
                        {eng.serviceDate
                          ? new Date(eng.serviceDate).toLocaleString()
                          : "N/A"}
                      </div>
                          <div
                            style={{ display: "flex", gap: 12, marginTop: 8 }}
                          >
                        <button
                        style={{
                            background: "#6C63FF",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "8px 18px",
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: "pointer",
                            alignSelf: "flex-start",
                        }}
                              onClick={() =>
                                navigate(`/engagement-chat/${eng.id}`)
                              }
                        >
                          Go to Chat
                        </button>
                        {/* Review Modal Trigger */}
                        {!eng.review && (
                          <button
                style={{
                  background: "#FFD700",
                  color: "#222",
                              border: "none",
                  borderRadius: 8,
                              padding: "8px 18px",
                              fontWeight: 700,
                              fontSize: 15,
                              cursor: "pointer",
                              alignSelf: "flex-start",
                }}
                            onClick={() =>
                                  setReviewModal({
                                    open: true,
                                    engagement: eng,
                                  })
                            }
                          >
                            Leave Review
                          </button>
            )}
          </div>
                      {reviewSuccess &&
                        reviewModal.engagement?.id === eng.id && (
                              <div
                                style={{
                                  color: "#155724",
                                  marginTop: 8,
                                  fontWeight: 500,
                                  padding: "8px 12px",
                                  background: "#d4edda",
                                  borderRadius: 6,
                                  border: "1px solid #c3e6cb",
                                }}
                              >
                            {reviewSuccess}
        </div>
                        )}
      </div>
                  ))
                )}
                {/* Review Modal */}
                {reviewModal.open && (
      <div
        style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      background: "rgba(0,0,0,0.25)",
                      zIndex: 2000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
        }}
                    onClick={() =>
                      setReviewModal({ open: false, engagement: null })
                    }
      >
        <div
          style={{
                        background: "#fff",
                        borderRadius: 16,
                        padding: 32,
                        minWidth: 340,
                        maxWidth: 400,
                        boxShadow: "0 4px 24px #bbb",
                        position: "relative",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                          <h2
                            style={{
                              marginBottom: 18,
                              color: "#6C63FF",
                              fontWeight: 800,
                              fontSize: 24,
                            }}
                          >
                        Leave a Review
                      </h2>
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          setReviewLoading(true);
                          setReviewSuccess("");
                          setReviewError("");
                          try {
                            const user = JSON.parse(
                              localStorage.getItem("user") || "null"
                            );
                            const clientId = user?.actorId;
                                if (!clientId)
                                  throw new Error("User not found");
                            await postClientReview(clientId, {
                                  serviceEngagementId:
                                    reviewModal.engagement.id,
                              rating: reviewForm.rating,
                              serviceQualityRating:
                                reviewForm.serviceQualityRating,
                              communicationRating:
                                reviewForm.communicationRating,
                              valueForMoneyRating:
                                reviewForm.valueForMoneyRating,
                              comment: reviewForm.comment,
                              wouldRecommend: reviewForm.wouldRecommend,
                            });
                                setReviewSuccess(
                                  "Review submitted successfully!"
                                );
                            setReviewForm({
                              rating: 0,
                              serviceQualityRating: 0,
                              communicationRating: 0,
                              valueForMoneyRating: 0,
                              comment: "",
                              wouldRecommend: false,
                            });
                            setTimeout(
                              () =>
                                setReviewModal({
                                  open: false,
                                  engagement: null,
                                }),
                              1200
                            );
                          } catch (err: any) {
                            setReviewError(
                              err.message || "Failed to submit review"
                            );
                          } finally {
                            setReviewLoading(false);
                          }
                        }}
                      >
                            <div style={{ marginBottom: 12 }}>
                              <label
                                style={{
                                  color: "#222",
                                  fontWeight: 600,
                                  display: "block",
                                  marginBottom: 6,
                                }}
                              >
                                Overall Rating:{" "}
                              </label>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              color={
                                    star <= reviewForm.rating
                                      ? "#FFD700"
                                      : "#eee"
                              }
            style={{
                                cursor: "pointer",
              fontSize: 22,
                                marginLeft: 2,
                              }}
                              onClick={() =>
                                    setReviewForm((f) => ({
                                      ...f,
                                      rating: star,
                                    }))
                              }
                            />
                          ))}
                        </div>
                            <div style={{ marginBottom: 12 }}>
                              <label
                                style={{
                                  color: "#222",
                                  fontWeight: 600,
                                  display: "block",
                                  marginBottom: 6,
                                }}
                              >
                                Service Quality:{" "}
                              </label>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              color={
                                star <= reviewForm.serviceQualityRating
                                  ? "#FFD700"
                                  : "#eee"
                              }
                              style={{
                                cursor: "pointer",
                                fontSize: 22,
                                marginLeft: 2,
                              }}
                              onClick={() =>
                                setReviewForm((f) => ({
                                  ...f,
                                  serviceQualityRating: star,
                                }))
                              }
                            />
                          ))}
                        </div>
                            <div style={{ marginBottom: 12 }}>
                              <label
                                style={{
                                  color: "#222",
                                  fontWeight: 600,
                                  display: "block",
                                  marginBottom: 6,
                                }}
                              >
                                Communication:{" "}
                              </label>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              color={
                                star <= reviewForm.communicationRating
                                  ? "#FFD700"
                                  : "#eee"
                              }
                              style={{
                                cursor: "pointer",
                                fontSize: 22,
                                marginLeft: 2,
                              }}
                              onClick={() =>
                                setReviewForm((f) => ({
                                  ...f,
                                  communicationRating: star,
                                }))
                              }
                            />
                          ))}
                        </div>
                            <div style={{ marginBottom: 12 }}>
                              <label
                                style={{
                                  color: "#222",
                                  fontWeight: 600,
                                  display: "block",
                                  marginBottom: 6,
                                }}
                              >
                                Value for Money:{" "}
                              </label>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              color={
                                star <= reviewForm.valueForMoneyRating
                                  ? "#FFD700"
                                  : "#eee"
                              }
                              style={{
                                cursor: "pointer",
                                fontSize: 22,
                                marginLeft: 2,
            }}
                              onClick={() =>
                                setReviewForm((f) => ({
                                  ...f,
                                  valueForMoneyRating: star,
                                }))
                              }
                            />
                          ))}
                        </div>
                            <div style={{ marginBottom: 12 }}>
                              <label
                                style={{
                                  color: "#222",
                                  fontWeight: 600,
                                  display: "block",
                                  marginBottom: 6,
                                }}
                              >
                                Comment:{" "}
                              </label>
                          <textarea
                            value={reviewForm.comment}
                            onChange={(e) =>
                              setReviewForm((f) => ({
                                ...f,
                                comment: e.target.value,
                              }))
                            }
                            required
                            style={{
                              width: "100%",
                                  minHeight: 80,
                                  borderRadius: 8,
                                  border: "1px solid #ddd",
                                  padding: 10,
                              marginTop: 4,
                                  fontSize: 14,
                                  color: "#333",
                                  fontFamily: "inherit",
                            }}
                          />
                        </div>
                            <div style={{ marginBottom: 12 }}>
                              <label
                                style={{
                                  color: "#222",
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  cursor: "pointer",
                                }}
                              >
                            <input
                              type="checkbox"
                              checked={reviewForm.wouldRecommend}
                              onChange={(e) =>
                                setReviewForm((f) => ({
                                  ...f,
                                  wouldRecommend: e.target.checked,
                                }))
                              }
                                  style={{
                                    cursor: "pointer",
                                    width: 16,
                                    height: 16,
                                    margin: 0,
                                    accentColor: "#6C63FF",
                                  }}
                            />
                            Would recommend
                          </label>
                        </div>
                        {reviewError && (
                              <div
                                style={{
                                  color: "#dc3545",
                                  marginBottom: 12,
                                  fontWeight: 500,
                                  padding: "8px 12px",
                                  background: "#f8d7da",
                                  borderRadius: 6,
                                  border: "1px solid #f5c6cb",
                                }}
                              >
                            {reviewError}
                          </div>
                        )}
                        {reviewSuccess && (
                              <div
                                style={{
                                  color: "#155724",
                                  marginBottom: 12,
                                  fontWeight: 500,
                                  padding: "8px 12px",
                                  background: "#d4edda",
                                  borderRadius: 6,
                                  border: "1px solid #c3e6cb",
                                }}
                              >
                            {reviewSuccess}
                          </div>
                        )}
          <button
                          type="submit"
                          disabled={reviewLoading}
            style={{
                            background: reviewLoading ? "#ccc" : "#6C63FF",
              color: "#fff",
              border: "none",
              borderRadius: 8,
                            padding: "8px 24px",
              fontWeight: 700,
                            fontSize: 15,
                                cursor: reviewLoading
                                  ? "not-allowed"
                                  : "pointer",
                            marginTop: 8,
                          }}
                        >
                              {reviewLoading
                                ? "Submitting..."
                                : "Submit Review"}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                                setReviewModal({
                                  open: false,
                                  engagement: null,
                                })
                          }
                          style={{
                            background: "#eee",
                            color: "#444",
                            border: "none",
                            borderRadius: 8,
                            padding: "8px 24px",
                            fontWeight: 600,
                            fontSize: 15,
              cursor: "pointer",
                            marginTop: 8,
                            marginLeft: 8,
            }}
          >
                          Cancel
          </button>
                      </form>
        </div>
                  </div>
                )}
              </div>
            )}
            {/* About Tab */}
            {activeTab === 3 && (
            <div
              style={{
                  background: "#fff",
                borderRadius: 16,
                boxShadow: "0 2px 8px #eee",
                  padding: 32,
                  fontSize: 16,
                      color: "#333",
                }}
              >
                <h3
                  style={{
                    color: "#6C63FF",
                    fontWeight: 800,
                        marginBottom: 20,
                        fontSize: 24,
                  }}
                >
                  About
                </h3>
                    <div
                      style={{
                        marginBottom: 16,
                        lineHeight: 1.6,
                        fontWeight: 400,
                      }}
                    >
                      {vendor.bio}
                    </div>
                    <div style={{ marginBottom: 12, fontWeight: 400 }}>
                      <strong style={{ color: "#222", fontWeight: 600 }}>
                        Years of Experience:
                      </strong>{" "}
                  {vendor.yearsOfExperience || "N/A"}
                </div>
                    <div style={{ marginBottom: 12, fontWeight: 400 }}>
                      <strong style={{ color: "#222", fontWeight: 600 }}>
                        Location:
                      </strong>{" "}
                      {vendor.city}, {vendor.state}, {vendor.country}
                </div>
                    <div style={{ marginBottom: 12, fontWeight: 400 }}>
                      <strong style={{ color: "#222", fontWeight: 600 }}>
                        Email:
                      </strong>{" "}
                      {vendor.userEmail}
                </div>
                    <div style={{ marginBottom: 12, fontWeight: 400 }}>
                      <strong style={{ color: "#222", fontWeight: 600 }}>
                        Website:
                      </strong>{" "}
                  <a
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                        style={{
                          color: "#6C63FF",
                          fontWeight: 500,
                          textDecoration: "none",
                        }}
                  >
                    {vendor.website}
                  </a>
                </div>
              </div>
            )}
              </div>
          </div>
        </div>
      </div>
      {/* Similar Vendors Section */}
      <div
        style={{
          maxWidth: 1300,
          margin: "48px auto 0 auto",
          padding: "0 16px",
        }}
      >
        <div
          style={{
            display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <div>
              <h3
              style={{
                  color: "#0f172a",
                  fontWeight: 800,
                  marginBottom: 6,
                  fontSize: 28,
                }}
              >
                Similar Vendors
              </h3>
              <p style={{ color: "#64748b", margin: 0 }}>
                Curated professionals that match this vendor’s style and
                expertise.
              </p>
            </div>
            <button
              onClick={handleViewServices}
                style={{
                background: "rgba(99,102,241,0.1)",
                color: "#6366f1",
                border: "1px solid rgba(99,102,241,0.2)",
                borderRadius: 999,
                padding: "10px 20px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Explore Services
            </button>
          </div>
          {similarLoading ? (
              <div
                style={{
                textAlign: "center",
                padding: 64,
                color: "#6366f1",
              }}
            >
              Fetching curated matches...
              </div>
          ) : similarError ? (
              <div
                style={{
                textAlign: "center",
                padding: 48,
                color: "#b91c1c",
                background: "rgba(248,113,113,0.1)",
                borderRadius: 20,
              }}
            >
              {similarError}
              </div>
          ) : similarVendors.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                padding: 48,
                color: "#64748b",
                borderRadius: 20,
                border: "1px dashed rgba(148,163,184,0.4)",
                }}
              >
              Similar vendors will appear here once available.
              </div>
          ) : (
              <div
                style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 24,
              }}
            >
              {similarVendors.map((sv) => (
                <div
                  key={sv.id}
                  style={{
                    background:
                      "linear-gradient(140deg, #0f172a 0%, #1e1b4b 100%)",
                    borderRadius: 24,
                    padding: 24,
                    boxShadow: "0 25px 60px rgba(2,6,23,0.65)",
                    color: "#f8fafc",
                  display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 14 }}
                  >
                    <img
                      src={getSimilarAvatar(sv)}
                      alt={formatSimilarName(sv)}
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "3px solid rgba(255,255,255,0.2)",
                      }}
                    />
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {formatSimilarName(sv)}
                      </div>
                      <div style={{ color: "#cbd5f5", fontSize: 14 }}>
                        {formatSimilarLocation(sv)}
                      </div>
                    </div>
                  </div>
                  {sv.bio && (
                    <p
                      style={{
                        fontSize: 14,
                        color: "rgba(248,250,252,0.8)",
                        lineHeight: 1.6,
                        minHeight: 60,
                      }}
                    >
                      {sv.bio}
                    </p>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      flexWrap: "wrap",
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      color: "#a5b4fc",
                    }}
                  >
                    <span>{sv.yearsOfExperience || 0}+ yrs exp</span>
                    <span>
                      {sv.isVerified ? "Verified" : sv.status || "Pending"}
                    </span>
              </div>
              <button
                    onClick={() => navigate(`/business-listing/${sv.id}`)}
                style={{
                      marginTop: "auto",
                      background:
                        "linear-gradient(120deg, #6366f1 0%, #8b5cf6 100%)",
                  border: "none",
                      borderRadius: 16,
                      padding: "12px 20px",
                      color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                      boxShadow: "0 10px 30px rgba(99,102,241,0.4)",
                }}
              >
                View Profile
              </button>
            </div>
          ))}
            </div>
          )}
        </div>
      </div>
      {/* Image Modal */}
      {/* Image Modal */}
      {modalImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setModalImage(null)}
        >
          <img
            src={modalImage}
            alt="Magnified"
            style={{
              maxWidth: "90vw",
              maxHeight: "80vh",
              borderRadius: 16,
              boxShadow: "0 8px 32px #2228",
              background: "#fff",
            }}
          />
        </div>
      )}
      {/* Business Listing Modal */}
      {modalListing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setModalListing(null)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 8px 32px #bbb",
              padding: 36,
              minWidth: 340,
              maxWidth: 500,
              width: "90vw",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalListing(null)}
              style={{
                position: "absolute",
                top: 12,
                right: 16,
                background: "none",
                border: "none",
                fontSize: 28,
                color: "#666",
                cursor: "pointer",
                fontWeight: 300,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#f0f0f0";
                e.currentTarget.style.color = "#222";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "none";
                e.currentTarget.style.color = "#666";
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h3
              style={{
                fontWeight: 800,
                fontSize: 22,
                marginBottom: 18,
                color: "#6C63FF",
              }}
            >
              {modalListing.businessName}
            </h3>
            {modalListing.images && modalListing.images.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                {modalListing.images.map((img: any, idx: number) => (
                  <img
                    key={idx}
                    src={img.imageUrl}
                    alt={modalListing.businessName + " image " + (idx + 1)}
                    style={{
                      width: 80,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                ))}
              </div>
            )}
            <div
              style={{
                color: "#6C63FF",
                fontWeight: 600,
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FaMapMarkerAlt /> {modalListing.city}, {modalListing.state}
            </div>
            <div
              style={{
                color: "#333",
                fontSize: 15,
                marginBottom: 12,
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              {modalListing.description}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 12,
              }}
            >
              {(modalListing.services || []).map((s: any, i: number) => (
                <span
                  key={i}
                  style={{
                    background: "#f8f8ff",
                    color: "#6C63FF",
                    borderRadius: 6,
                    padding: "4px 12px",
                    fontWeight: 600,
                    fontSize: 15,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaCheckCircle color="#28a745" style={{ fontSize: 13 }} />{" "}
                  {s.serviceName}
                </span>
              ))}
            </div>
            <div
              style={{
                color: "#333",
                fontSize: 14,
                marginBottom: 8,
                fontWeight: 400,
              }}
            >
              <strong style={{ color: "#222", fontWeight: 600 }}>
                Contact:
              </strong>{" "}
              {modalListing.contactEmail} | {modalListing.contactPhone}
            </div>
            <div
              style={{
                color: "#333",
                fontSize: 14,
                marginBottom: 8,
                fontWeight: 400,
              }}
            >
              <strong style={{ color: "#222", fontWeight: 600 }}>
                Address:
              </strong>{" "}
              {modalListing.address}
            </div>
            <div
              style={{
                color: "#333",
                fontSize: 14,
                marginBottom: 8,
                fontWeight: 400,
              }}
            >
              <strong style={{ color: "#222", fontWeight: 600 }}>
                Price Range:
              </strong>{" "}
              {modalListing.minPrice} - {modalListing.maxPrice}{" "}
              {modalListing.currency}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                style={{
                  background: "#6C63FF",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 14px",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  minWidth: 0,
                }}
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Booking Modal */}
      {bookingModal.open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setBookingModal({ open: false, biz: null })}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 32,
              minWidth: 340,
              maxWidth: 400,
              boxShadow: "0 4px 24px #bbb",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                marginBottom: 18,
                color: "#6C63FF",
                fontWeight: 800,
                fontSize: 24,
              }}
            >
              Book Service
            </h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setBookingLoading(true);
                setBookingError("");
                setBookingSuccess("");
                try {
                  const user = JSON.parse(
                    localStorage.getItem("user") || "null"
                  );
                  if (!user?.id) throw new Error("User not found");
                  const service = (bookingModal.biz?.services || [])[0];
                  if (!service?.id) throw new Error("Service not found");
                  const payload = {
                    clientId: user.id,
                    businessServiceId: service.id,
                    inquiryMessage: bookingForm.inquiryMessage,
                    preferredServiceDate: bookingForm.preferredServiceDate,
                    estimatedPrice: bookingForm.estimatedPrice
                      ? Number(bookingForm.estimatedPrice)
                      : undefined,
                    currency: bookingForm.currency,
                  };
                  const res = await createServiceEngagement(payload);
                  setBookingModal({ open: false, biz: null });
                  setBookingForm({
                    inquiryMessage: "",
                    preferredServiceDate: "",
                    estimatedPrice: "",
                    currency: "NGN",
                  });
                  setBookingSuccess(
                    "Your engagement was submitted successfully!"
                  );
                  setLastEngagementId(res.data.id?.toString() || null);
                } catch (err: any) {
                  setBookingError(err.message || "Booking failed");
                } finally {
                  setBookingLoading(false);
                }
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    fontWeight: 600,
                    color: "#222",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Inquiry Message
                </label>
                <textarea
                required
                  value={bookingForm.inquiryMessage}
                onChange={(e) =>
                    setBookingForm((f) => ({
                      ...f,
                      inquiryMessage: e.target.value,
                    }))
                }
                style={{
                    width: "100%",
                    minHeight: 80,
                  borderRadius: 8,
                    border: "1px solid #ddd",
                    padding: 10,
                    marginTop: 4,
                    fontSize: 14,
                    color: "#333",
                    fontFamily: "inherit",
                }}
                  placeholder="Describe your event needs..."
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    fontWeight: 600,
                    color: "#222",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Preferred Service Date
                </label>
                <input
                  type="datetime-local"
                required
                  value={bookingForm.preferredServiceDate}
                onChange={(e) =>
                    setBookingForm((f) => ({
                      ...f,
                      preferredServiceDate: e.target.value,
                    }))
                }
                style={{
                    width: "100%",
                  borderRadius: 8,
                    border: "1px solid #ddd",
                    padding: 10,
                    marginTop: 4,
                    fontSize: 14,
                    color: "#333",
                  }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    fontWeight: 600,
                    color: "#222",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Estimated Price (optional)
                </label>
                <input
                  type="number"
                  min="0"
                  value={bookingForm.estimatedPrice}
                  onChange={(e) =>
                    setBookingForm((f) => ({
                      ...f,
                      estimatedPrice: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    padding: 10,
                    marginTop: 4,
                    fontSize: 14,
                    color: "#333",
                  }}
                  placeholder="e.g. 400000"
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    fontWeight: 600,
                    color: "#222",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Currency
                </label>
                <select
                  value={bookingForm.currency}
                  onChange={(e) =>
                    setBookingForm((f) => ({
                      ...f,
                      currency: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    padding: 10,
                    marginTop: 4,
                    fontSize: 14,
                    color: "#333",
                    background: "#fff",
                  }}
                >
                  <option value="NGN">NGN</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              {bookingError && (
                <div
                  style={{
                    color: "#dc3545",
                    marginBottom: 12,
                    fontWeight: 500,
                    padding: "8px 12px",
                    background: "#f8d7da",
                    borderRadius: 6,
                    border: "1px solid #f5c6cb",
                  }}
                >
                  {bookingError}
                </div>
              )}
              {bookingSuccess && (
                <div
                  style={{
                    color: "#155724",
                    marginBottom: 12,
                    fontWeight: 500,
                    padding: "8px 12px",
                    background: "#d4edda",
                    borderRadius: 6,
                    border: "1px solid #c3e6cb",
                  }}
                >
                  {bookingSuccess}
                </div>
              )}
              <button
                type="submit"
                disabled={bookingLoading}
                style={{
                  background: bookingLoading ? "#ccc" : "#6C63FF",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 24px",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: bookingLoading ? "not-allowed" : "pointer",
                  width: "100%",
                }}
              >
                {bookingLoading ? "Booking..." : "Book Now"}
              </button>
              <button
                type="button"
                onClick={() => setBookingModal({ open: false, biz: null })}
                style={{
                  background: "#eee",
                  color: "#444",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 24px",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer",
                  width: "100%",
                  marginTop: 10,
                }}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessListingDetails;
